"use client";

import Button, { ButtonSize, ButtonVariant } from "@/app/(components)/generics/Button/Button";
import EntityDetail, {
  DetailGrid,
  DetailRow,
  DetailSection,
  EntityDetailHeader,
} from "@/app/(components)/generics/EntityDetail/EntityDetail";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import PatientInsoles from "@/app/(components)/specifics/patients/Patients/components/PatientInsoles/PatientInsoles";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PublicPatient } from "@/app/types/PublicPatient";
import styles from "./PatientDetail.module.css";

type Props = {
  patient: PublicPatient;
  lang: string;
  onEdit?: (patient: PublicPatient) => void;
  onDelete?: (patient: PublicPatient) => void;
};

function getInitials(patient: PublicPatient): string {
  return (patient.firstName.at(0) ?? "") + (patient.lastName.at(0) ?? "");
}

function formatDate(date: Date | null, lang: string): string {
  if (!date) return "";
  return new Date(date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function computeAge(date: Date | null): number | null {
  if (!date) return null;
  const today = new Date();
  const birth = new Date(date);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function getGenderLabel(gender: string | null, lang: string): string | null {
  if (!gender) return null;
  const map: Record<string, TKey> = {
    MALE: TKey.PATIENT_GENDER_MALE,
    FEMALE: TKey.PATIENT_GENDER_FEMALE,
    OTHER: TKey.PATIENT_GENDER_OTHER,
  };
  return map[gender] ? (T(map[gender], lang) as string) : null;
}

export default function PatientDetail({ patient, lang, onEdit, onDelete }: Props) {
  const age = computeAge(patient.birthDate);
  const genderLabel = getGenderLabel(patient.gender, lang);
  const hasMeasurements = patient.shoeSize || patient.footSize || patient.weight;

  const subHeaderParts = [genderLabel, age !== null ? `${age} ans` : null].filter(Boolean);

  const sections: DetailSection[] = [
    {
      content: (
        <DetailRow
          label={T(TKey.PATIENT_BIRTH_DATE, lang) as string}
          value={
            patient.birthDate
              ? formatDate(patient.birthDate, lang)
              : (T(TKey.PATIENT_NO_DATA, lang) as string)
          }
          muted={!patient.birthDate}
        />
      ),
    },
    ...(hasMeasurements
      ? [
          {
            title: T(TKey.PATIENT_MEASUREMENTS, lang) as string,
            content: (
              <DetailGrid>
                {patient.shoeSize ? (
                  <DetailRow
                    label={T(TKey.PATIENT_SIZE_EU, lang) as string}
                    value={patient.shoeSize}
                  />
                ) : null}
                {patient.footSize ? (
                  <DetailRow
                    label={T(TKey.PATIENT_SIZE_MM, lang) as string}
                    value={`${patient.footSize} mm`}
                  />
                ) : null}
                {patient.weight ? (
                  <DetailRow
                    label={T(TKey.PATIENT_WEIGHT, lang) as string}
                    value={`${patient.weight} kg`}
                  />
                ) : null}
              </DetailGrid>
            ),
          },
        ]
      : []),
    ...(patient.notes
      ? [
          {
            title: T(TKey.PATIENT_NOTES, lang) as string,
            content: <p className={styles.detailNotes}>{patient.notes}</p>,
          },
        ]
      : []),
  ];

  const overview = (
    <EntityDetail
      hideHeader
      initials={getInitials(patient)}
      name={`${patient.firstName} ${patient.lastName}`}
      sections={sections}
      actions={
        onEdit || onDelete ? (
          <>
            {onEdit && (
              <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SM}
                onClick={() => onEdit(patient)}
              >
                {T(TKey.EDIT_PATIENT, lang)}
              </Button>
            )}
            {onDelete && (
              <Button
                variant={ButtonVariant.DANGER}
                size={ButtonSize.SM}
                onClick={() => onDelete(patient)}
              >
                {T(TKey.DELETE_PATIENT, lang)}
              </Button>
            )}
          </>
        ) : undefined
      }
    />
  );

  return (
    <div className={styles.detail}>
      <EntityDetailHeader
        initials={getInitials(patient)}
        name={`${patient.firstName} ${patient.lastName}`}
        subHeader={
          subHeaderParts.length > 0 ? (
            <span className={styles.detailAge}>{subHeaderParts.join(" · ")}</span>
          ) : undefined
        }
      />

      <TabbedContent
        items={[
          { id: "overview", title: T(TKey.OVERVIEW, lang) as string, content: overview },
          {
            id: "insoles",
            title: T(TKey.INSOLES, lang) as string,
            content: <PatientInsoles patient={patient} lang={lang} />,
          },
        ]}
      />
    </div>
  );
}
